import { inject, Injectable } from '@angular/core';
import {
  CapacityReportOrderByInput,
  CapacityReportStatus,
  FetchCapacityReportArgs,
  Sort,
} from '@libs/services/graphql/generated/ala-backend.service';
import { Mutable } from '@libs/services/graphql/utilities/to-mutable';
import { CapacityReportingDto } from '../../dtos-v2/capacity-reporting.dto';
import { constants } from '../../shared/constants/constants';
import { CapacityTransformerService } from '../../transformers-v2/capacity.transformer.service';
import { CapacityService } from '../capacity.service';
import { BaseSignalStore, BaseState } from './base/base-signal-store.service';
/**
 * CapacityReportListStateService
 * --------------------------------
 * This service manages the state for a paginated and sortable list of capacity reports.
 * It uses Angular signals via the base SignalStore for reactive state management.
 *
 * Features:
 * - Reactive state container with selectors
 * - Async loading of reports with loading/error signals
 * - State patching via setters
 * - Computed signals like `skip`, `take`, `hasCapacityReports`
 */
@Injectable({ providedIn: 'root' })
export class CapacityReportListStateService extends BaseSignalStore<ICapacityReportListState> {
  // Inject dependencies via Angular DI
  private readonly service = inject(CapacityService); // Service to fetch data from backend
  private readonly _transformerService = inject(CapacityTransformerService);

  constructor() {
    super({
      // Initial state
      filteredCapacityReports: [],
      filteredCapacityReportsCount: 0,
      gridState: {
        tab: [CapacityReportStatus.INPROGRESS, CapacityReportStatus.NOTSTARTED],
        page: 1,
        perPageCount: constants.VIEW_ENTRIES_PERPAGE_COUNT[0],
        sort: { capacityReportPeriodDueDate: Sort.ASC },
      },
      showProgressIndicator: false,
      isLoading: false,
      error: '',
    });
  }

  //#region === SETTERS ====
  // Each setter updates the state immutably using `patch`

  /** Set filtered capacity reports after fetching from backend */
  set setFilteredCapacityReports(
    filteredCapacityReports: CapacityReportingDto[]
  ) {
    this.patchState({ filteredCapacityReports });
  }

  /** Set total count of filtered capacity reports */
  set setFilteredCapacityReportsCount(filteredCapacityReportsCount: number) {
    this.updateState((state) => {
      const currentPerPage = state.gridState.perPageCount;
      const clampedPerPage =
        currentPerPage > filteredCapacityReportsCount
          ? filteredCapacityReportsCount
          : currentPerPage;
      return {
        ...state,
        filteredCapacityReportsCount,
        gridState: {
          ...state.gridState,
          perPageCount: clampedPerPage,
        },
      };
    });
  }

  /** Set current page number */
  set setPage(page: number) {
    this.updateState((state) => ({
      ...state,
      gridState: {
        ...state.gridState,
        page,
      },
    }));
  }

  /** Set active tab (0 = NotStarted/InProgress, 2 = Completed) and reset page/perPageCount */
  set setTab(tabIndex: number) {
    const tab =
      tabIndex === 2
        ? [CapacityReportStatus.COMPLETED]
        : [CapacityReportStatus.NOTSTARTED, CapacityReportStatus.INPROGRESS];

    this.updateState((state) => ({
      ...state,
      gridState: {
        ...state.gridState,
        tab,
        page: 1, // Reset to first page
        perPageCount: constants.VIEW_ENTRIES_PERPAGE_COUNT[0],
      },
    }));
  }

  /** Set items per page */
  set setPerPageCount(perPageCount: number) {
    this.updateState((state) => ({
      ...state,
      gridState: {
        ...state.gridState,
        perPageCount,
      },
    }));
  }

  /** Set sorting order for the list */
  set setSortOrder(sort: Mutable<CapacityReportOrderByInput>) {
    this.updateState((state) => ({
      ...state,
      gridState: {
        ...state.gridState,
        sort,
      },
    }));
  }

  /** Show or hide the progress/loading indicator */
  set setShowProgressIndicator(showProgressIndicator: boolean) {
    this.patchState({ showProgressIndicator });
  }
  //#endregion === SETTERS ====

  //#region === SELECTORS ===
  // Reactive signals to be used in components/templates

  /** Watch entire state */
  readonly stateWatcher = this.select((s) => s);

  /** Filtered capacity reports */
  readonly filteredCapacityReports = this.select(
    (s) => s.filteredCapacityReports
  );

  /** Total count of filtered reports */
  readonly facilitiesCount = this.select((s) => s.filteredCapacityReportsCount);

  /** Options for per-page dropdown based on total count */
  readonly pageCountOptions = this.select((state) => {
    const opts = constants.VIEW_ENTRIES_PERPAGE_COUNT.filter(
      (size) => size < state.filteredCapacityReportsCount
    );

    if (opts.length < constants.VIEW_ENTRIES_PERPAGE_COUNT.length) {
      opts.push(state.filteredCapacityReportsCount);
    }

    return opts;
  });

  /** Boolean indicating if there are any reports */
  readonly hasCapacityReports = this.select(
    (s) => s.filteredCapacityReports.length > 0
  );

  /** Current page */
  readonly page = this.select((s) => s.gridState.page);
  /** Current tab selection */
  readonly tab = this.select((s) => s.gridState.tab);
  /** Items per page */
  readonly perPageCount = this.select((s) => s.gridState.perPageCount);
  /** Sort order */
  readonly sort = this.select((s) => s.gridState.sort);
  /** Loading indicator */
  readonly showProgressIndicator = this.select((s) => s.showProgressIndicator);

  /** Computed skip value for pagination */
  readonly skip = this.select(
    (s) => (s.gridState.page - 1) * s.gridState.perPageCount
  );
  /** Computed take value for pagination */
  readonly take = this.select((s) => s.gridState.perPageCount || 10);
  /** Computed orderBy for API call */
  readonly orderBy = this.select((s) => s.gridState.sort);

  //#endregion

  //#region === ACTIONS ===
  /** Build search criteria for fetching reports */

  updateCapacityReportStatus(
    id: string,
    newStatus: CapacityReportStatus
  ): void {
    const currentReports = this.filteredCapacityReports();

    const updatedReports = currentReports.map((report) =>
      report.id === id ? { ...report, status: newStatus } : report
    );

    this.patchState({ filteredCapacityReports: updatedReports });
  }

  getSearchCriteria(): FetchCapacityReportArgs {
    return {
      skip: this.skip(),
      take: this.take(),
      orderBy: this.orderBy(),
      statuses: this.tab(),
    };
  }

  /** Load reports and count asynchronously */
  async loadCapacityReports() {
    return this.executeAsync(async () => {
      const criteria = this.getSearchCriteria();

      // Fetch report list
      const reports = await this.service.fetchCapacityReports(criteria);
      if (reports) {
        const transformedDtos = reports.map((r) =>
          this._transformerService.transformToCapacityReportingDto(r)
        );
        this.setFilteredCapacityReports = transformedDtos;
      }

      // Fetch total count
      const count = await this.service.fetchCapacityReportsCount(criteria);
      if (count !== null && count !== undefined) {
        this.setFilteredCapacityReportsCount = count;
      }
    });
  }
  //#endregion
}

/** State interface for CapacityReportListStateService */
export interface ICapacityReportListState extends BaseState {
  filteredCapacityReports: CapacityReportingDto[];
  filteredCapacityReportsCount: number;
  showProgressIndicator: boolean;
  gridState: IGridState;
}

export interface IGridState {
  tab: CapacityReportStatus[];
  page: number;
  perPageCount: number;
  sort: Mutable<CapacityReportOrderByInput>;
}
